import { TicketInfo } from '@Config/ticket-form-types'
import { AwaitingNextDrawModalProps } from './awaiting-next-draw'
import { AddOnDetails } from '@Components/ticket-list/ticket-form-summary/ticket-form-summary'
import { ScoredDrawResultComplete, UnscoredDrawResult } from '@API/game-results/types'
import { AddOnHostConfig } from '@AppState/host-config'
import { ListViewMatchDetails } from '@Components/ticket-list/list-view/list-view'
import { MatchStatus } from '@Components/ticket-list/number-selection-summary/number-selection-summary-types'

const mapDrawToProps = (
  draw: UnscoredDrawResult,
  addOnConfigs: AddOnHostConfig[],
): AwaitingNextDrawModalProps => {
  const baseMatchDetails = draw.winningNumbers
    .map((num) => {
      return {
        number: num,
        matchStatus: MatchStatus.UNKNOWN,
        addOnName: null,
      }
    })
    .sort((a, b) => a.number - b.number)
  const extraDrawMatchDetails = draw.extraDrawAddOnResult
    ? draw.extraDrawAddOnResult?.results
        .map((res) => {
          return {
            number: res.result,
            matchStatus: MatchStatus.UNKNOWN,
            addOnName: draw.extraDrawAddOnResult?.name ?? null,
          }
        })
        .sort((a, b) => a.number - b.number)
    : []

  return {
    drawingNumber: draw.drawingNumber,
    latestDrawNumber: 0,
    matchDetails: [...baseMatchDetails, ...extraDrawMatchDetails],
    playerSelectedNumbers: [],
    multiplierAddOnResult: draw.multiplierAddOnResult,
    addOnDetails: addOnConfigs
      .filter((config) => config.name === draw.extraDrawAddOnResult?.name)
      .map((config) => {
        return { name: config.name, color: config.listResultColor ?? null }
      }),
  }
}

const mapCompletedTicketToProps = (
  ticketInfo: TicketInfo,
  lastCompleteResult: ScoredDrawResultComplete,
  addOnConfig: AddOnHostConfig[],
): AwaitingNextDrawModalProps => {
  const addOns: AddOnDetails[] = []

  const multiplier = ticketInfo.multiplierAddOnSelection
  if (multiplier) {
    addOns.push({
      name: multiplier.name,
      color: null,
    })
  }

  const extraDraw = ticketInfo.extraDrawAddOnSelection
  if (extraDraw) {
    const addonColor = addOnConfig.find((val) => val.name === extraDraw.name)?.listResultColor

    addOns.push({
      name: extraDraw.name,
      color: addonColor ? addonColor : null,
    })
  }

  const miniGame = ticketInfo.miniGameAddOnSelection
  if (miniGame) {
    addOns.push({
      name: miniGame.name,
      color: null,
    })
  }

  const baseResults = lastCompleteResult.winningNumbers
    .filter((res) => !res.addonName)
    .map((sel) => {
      const isMatch =
        ticketInfo.numberSelections.find((val) => val === sel.numberSelection) !== undefined
      const matchDetails: ListViewMatchDetails = {
        number: sel.numberSelection,
        matchStatus: isMatch ? MatchStatus.MATCH : MatchStatus.UNMATCHED,
        addOnName: null,
      }
      return matchDetails
    })
    .sort((a, b) => a.number - b.number)

  const extraResults = lastCompleteResult.winningNumbers
    .filter((res) => extraDraw && res.addonName === extraDraw?.name)
    .map((sel) => {
      const isMatch =
        ticketInfo.numberSelections.find((val) => val === sel.numberSelection) !== undefined
      const matchDetails: ListViewMatchDetails = {
        number: sel.numberSelection,
        matchStatus: isMatch ? MatchStatus.MATCH : MatchStatus.UNMATCHED,
        addOnName: null,
      }
      return matchDetails
    })
    .sort((a, b) => a.number - b.number)

  return {
    drawingNumber: lastCompleteResult.drawingNumber,
    latestDrawNumber: 0,
    playerSelectedNumbers: ticketInfo.numberSelections,
    multiplierAddOnResult: lastCompleteResult.multiplierAddOnResult,
    matchDetails: [...baseResults, ...extraResults],
    addOnDetails: addOns,
  }
}

export const AwaitingNextDrawUtils = {
  mapCompletedTicketToProps,
  mapDrawToProps,
}
